import React from 'react';

import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';

import PromptMessage from '@/components/UI/PromptMessage';
import SwitchField from '@/components/form/switch/SwitchField';
import { Button } from '@/components/buttons/Button';
import { useBankIdProfile } from '@/hooks/useBankIdProfile';
import { useDispatch, useSelector } from 'react-redux';
import { useApiV2Request } from '@/hooks/useApiV2Request';
import {
  selectIsEmailConsented,
  selectIsTextMessageConsented,
  selectSmsEmailConsents,
} from '@/store/selectors/consentsSelectors';
import { getConsents } from '@/store/actions/consentsActions';
import ApiService from '@/shared/apiService';

interface FormValues {
  allowSms: boolean;
  allowEmail: boolean;
}

const SmsEmailMarketing = () => {
  const intl = useIntl();
  const { isCompletedBankIdProfile } = useBankIdProfile();
  const dispatch = useDispatch();
  const { success, loading, error, request: saveConsents } = useApiV2Request();
  const isSmsConsented = useSelector(selectIsTextMessageConsented);
  const isEmailConsented = useSelector(selectIsEmailConsented);
  const consents = useSelector(selectSmsEmailConsents);

  const handleOnSubmit = (values: FormValues) => {
    const updatedUserConsents = [...consents].map(consent => {
      return {
        ConsentId: consent.ConsentId,
        Version: consent.Version,
        Consented: consent.Name === 'TEXT_MESSAGE' ? values.allowSms : values.allowEmail,
      };
    });

    saveConsents(ApiService.saveConsents, updatedUserConsents, () => {
      dispatch(getConsents());
    });
  };

  const initialValues: FormValues = { allowSms: !!isSmsConsented, allowEmail: !!isEmailConsented };

  // this flag is true per default for all non-regulated locales
  if (!isCompletedBankIdProfile) {
    return null;
  }

  return (
    <section
      className="mx-auto mb-4 max-w-[720px] flex flex-wrap justify-between py-8 px-4 bg-white rounded-md
      sm:p-8 sm:pb-8"
    >
      <h2 className="w-full mt-0 text-[22px]">
        {intl.formatMessage({
          id: 'settings.details.receiveOffersAndPromotions.header',
        })}
      </h2>
      <Formik initialValues={initialValues} onSubmit={handleOnSubmit} enableReinitialize>
        <Form className="w-full flex flex-col gap-5 mt-5">
          <SwitchField
            name="allowEmail"
            label={intl.formatMessage({ id: 'inputs.toggle.email' })}
            labelClassName="text-blue-blueMedium"
          />
          <SwitchField
            name="allowSms"
            label={intl.formatMessage({ id: 'inputs.toggle.sms' })}
            labelClassName="text-blue-blueMedium"
          />
          <div className="buttons">
            {success && (
              <PromptMessage
                type="success"
                message={intl.formatMessage({
                  id: 'settings.details.successMessage',
                })}
              />
            )}
            {error && <p>Consents could not be saved</p>}
            <Button
              text={intl.formatMessage({ id: 'settings.details.saveButton' })}
              type="submit"
              btnType="primary"
              isLoading={loading}
            />
          </div>
        </Form>
      </Formik>
    </section>
  );
};

export default SmsEmailMarketing;
